import { GetServerSideProps, GetServerSidePropsContext } from 'next';
import { getStaticContent } from '@/actions/ecom/staticContent';
import PageLayout from '@/components/Layouts/PageLayout';
import useLayoutRenderer from '@/hooks/useLayoutRenderer';
import SharedStaticContent from '@/components/Static/Shared/SharedStaticContent';
import { FC, MemoExoticComponent } from 'react';
import StaticImageBanner from '@/components/Static/Shared/StaticImageBanner';
import Heading from '@/shared/Heading';
import Div from '@/shared/Div';
import Container from '@/shared/Container';
import getSingleString from '@/utils/string';
import { CMS_COMPONENTS } from '@/constants/cms';
import DynamicPlaceholder from '@/shared/crisp/DynamicPlaceholder';
import { useRouter } from 'next/router';
import classNames from 'classnames';
import TitleBanner from '@/shared/TitleBanner';
import LeftArrowIcon from '@/components/Icons/LeftArrow';
import Link from '@/shared/Link';
import SingleImagesBanner from '@/shared/crisp/SingleImagesBanner';
import { useAppConfigContext } from '@/context/AppContext';
import dlv from 'dlv';
interface PageProps {
  page: {
    layout: any;
    seo: any;
    title: any;
  };
}

export const getServerSideProps: GetServerSideProps = async (
  context: GetServerSidePropsContext,
) => {
  const { query } = context;
  const { slug } = query;
  let url = '';
  if (slug && Array.isArray(slug)) {
    url = '/' + slug?.join('/');
  } else {
    url = getSingleString(slug);
  }

  try {
    const page = await getStaticContent(url);
    return {
      props: {
        page,
      },
    };
  } catch (error) {
    return {
      notFound: true,
    };
  }
};

const STATIC_MAP: { [key: string]: MemoExoticComponent<any> | FC<any> } = {
  [CMS_COMPONENTS.SHARED.TITLE_BANNER]: TitleBanner,
  [CMS_COMPONENTS.SHARED.HTML]: SharedStaticContent,
  [CMS_COMPONENTS.STATIC.IMAGE_BANNER]: StaticImageBanner,
  [CMS_COMPONENTS.SHARED.DYNAMIC_PLACEHOLDER]: DynamicPlaceholder,
  [CMS_COMPONENTS.BANNERS.SINGE_IMAGE]: SingleImagesBanner,
};

const StaticPage: FC<PageProps> = ({ page }) => {
  const { layout, title } = page;
  const router = useRouter();
  const { state: appConfig } = useAppConfigContext();
  const renderLayout = useLayoutRenderer(layout || [], STATIC_MAP);
  // Investor relations page detection
  const path = router.asPath;
  const investorBase = '/investor-relations';
  const isInvestorPage = path.includes(investorBase);
  const subPageSlug = path.split(`${investorBase}/`)[1] || '';
  const isInvestorSubPage = isInvestorPage && subPageSlug.length > 0;

  const { dskImage, mobImage } = dlv(appConfig, 'config.investor-relations', {});

  return (
    <PageLayout seo={page?.seo} pageWrapperClassName="font-primary ">
      <Div
        className={classNames('static-page-container font-primary', {
          'investor-page': isInvestorPage || path === '/press-release',
          'investor-subpage': isInvestorSubPage || path === '/press-release',
        })}
      >
        {dskImage && mobImage && (isInvestorSubPage || path === '/press-release') ? (
          <SingleImagesBanner
            mobileImage={{
              data: {
                attributes: {
                  url: mobImage,
                },
              },
            }}
            desktopImage={{
              data: {
                attributes: {
                  url: dskImage,
                },
              },
            }}
          />
        ) : null}

        {title ? (
          <Container>
            {isInvestorSubPage || path === '/press-release' ? (
              <Div className="back-arrow-container my-s6">
                <Link href={investorBase} className="back-arrow-link">
                  <LeftArrowIcon />
                </Link>
                <Heading level={1} className="!text-h-2 md:!text-hd-2 text-cti-primary">
                  {title}
                </Heading>
              </Div>
            ) : (
              <Heading level={1} className="mt-s4 mb-s4">
                {title}
              </Heading>
            )}
          </Container>
        ) : null}
        {renderLayout}
      </Div>
    </PageLayout>
  );
};

export default StaticPage;
